import { useCallback, useEffect, useMemo, useState } from 'react';
import { GameVersion } from '../models/version';
import type { BuildStateMessage } from '../models/pipelineEvents';
import {
  ActivateVersionResult,
  listVersions,
  activateVersion as apiActivate,
  publishVersion as apiPublish,
  unpublishProject as apiUnpublish,
  playVersion as apiPlay,
  buildVersion as apiBuild,
} from '../services/versions';
import { isEngineUpgradeRequiredError } from '../services/engineUpgrade';
import { actionFailureMessage } from '../utils/actionFailure';

export interface UseVersionsResult {
  versions: GameVersion[];
  activeVersionId: string | null;
  publishedVersionId: string | null;
  isLoading: boolean;
  busyVersionId: string | null;
  error: string | null;
  refresh: () => Promise<void>;
  activate: (versionId: string) => Promise<void>;
  publish: (versionId: string) => Promise<void>;
  onBuildState: (msg: BuildStateMessage) => void;
  unpublish: () => Promise<void>;
  restore: (versionId: string) => Promise<void>;
  play: (versionId: string) => Promise<{ isReady: boolean; webGlPath: string | null }>;
}

export function useVersions(
  projectId: string,
  onActiveVersionChanged?: (result: ActivateVersionResult) => void,
): UseVersionsResult {
  const [versions, setVersions] = useState<GameVersion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [busyVersionId, setBusyVersionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!projectId) return;
    try {
      const list = await listVersions(projectId);
      setVersions(list);
    } catch {
      setError('Failed to load versions.');
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const runAction = useCallback(
    async (versionId: string, action: () => Promise<unknown>, failure: string) => {
      setError(null);
      setBusyVersionId(versionId);
      try {
        await action();
        await refresh();
      } catch (err) {
        if (isEngineUpgradeRequiredError(err)) {
          setError('This version was made with an older engine version, so it can’t be built.');
          await refresh();
        } else {
          setError(actionFailureMessage(err, failure));
        }
      } finally {
        setBusyVersionId(null);
      }
    },
    [refresh],
  );

  const activate = useCallback(
    async (versionId: string) => {
      setError(null);
      setBusyVersionId(versionId);
      try {
        const result = await apiActivate(projectId, versionId);
        await refresh();
        onActiveVersionChanged?.(result);
      } catch (err) {
        setError(actionFailureMessage(err, 'Failed to switch version.'));
      } finally {
        setBusyVersionId(null);
      }
    },
    [projectId, refresh, onActiveVersionChanged],
  );

  const publish = useCallback(
    (versionId: string) => runAction(versionId, () => apiPublish(projectId, versionId), 'Failed to publish this version.'),
    [projectId, runAction],
  );

  const onBuildState = useCallback(
    (msg: BuildStateMessage) => {
      if (msg.purpose !== 'Published') return;
      if (msg.state === 'Failed') {
        setError('Publishing failed — the public still plays the version published before.');
      }
      refresh();
    },
    [refresh],
  );

  const unpublish = useCallback(
    () => runAction('__unpublish__', () => apiUnpublish(projectId), 'Failed to unpublish this project.'),
    [projectId, runAction],
  );

  const restore = useCallback(
    (versionId: string) => runAction(versionId, () => apiBuild(projectId, versionId, 'WebGL'),
      'Couldn’t restore this version. A build may already be running, or it can’t be rebuilt yet.'),
    [projectId, runAction],
  );

  const play = useCallback(
    (versionId: string) => apiPlay(projectId, versionId),
    [projectId],
  );

  // While any version is (re)building, poll so the card flips from "Restoring…" to "Play" — and so a
  // version evicted by retention during that build flips to "Restore" — without a manual refresh.
  useEffect(() => {
    const isBuilding = (v: GameVersion) =>
      !v.isInProgress && (v.webBuildState === 'Building' || v.publishedBuildState === 'Building');
    if (!versions.some(isBuilding)) return;
    const interval = setInterval(refresh, 4000);
    return () => clearInterval(interval);
  }, [versions, refresh]);

  const activeVersionId = useMemo(() => versions.find((v) => v.isActive)?.id ?? null, [versions]);
  const publishedVersionId = useMemo(() => versions.find((v) => v.isPublished)?.id ?? null, [versions]);

  return {
    versions,
    activeVersionId,
    publishedVersionId,
    isLoading,
    busyVersionId,
    error,
    refresh,
    activate,
    publish,
    onBuildState,
    unpublish,
    restore,
    play,
  };
}
